import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip, Tabs, Tab,
  Grid, Divider, Stack, Avatar, AvatarGroup, Tooltip, CircularProgress,
  List, ListItem, ListItemAvatar, ListItemText, Alert, LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Edit as EditIcon,
  Delete as DeleteIcon, Description as DescIcon, Download as DownloadIcon,
  UploadFile as UploadFileIcon, Timeline as TimelineIcon,
  Groups as GroupsIcon, TableChart as TableChartIcon, Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiServices';
import { printProjectTime, exportProjectTimeExcel, exportProjectTimePdf } from '../../utils/projectTimeExport';
import Toast from '../actions/Toasts';
import {
  departmentLabel, statusOption, priorityOption, fmtDate, workerName,
} from './projectConstants';
import useConfirm from '../../hooks/useConfirm';
import ProjectFormDialog from './ProjectFormDialog';
import { activeWorkers } from '../../utils/workerStatus';

const API_URL = process.env.REACT_APP_API_URL || '';

function authToken() {
  try {
    const raw = sessionStorage.getItem('logedInUser') || '';
    if (raw) return JSON.parse(raw)?.token || '';
    const cookie = document.cookie.split(';').find((c) => c.trim().startsWith('logedInUser='));
    if (cookie) return JSON.parse(decodeURIComponent(cookie.split('=').slice(1).join('=')))?.token || '';
  } catch {}
  return '';
}

const fmtDateTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d).slice(0, 16).replace('T', ' ');
  return dt.toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const ACTIVITY_LABELS = {
  project_created:    'Projekti u krijua',
  project_updated:    'Projekti u përditësua',
  status_changed:     'Statusi u ndryshua',
  priority_changed:   'Prioriteti u ndryshua',
  department_changed: 'Departamenti u ndryshua',
  manager_changed:    'Menaxheri u ndryshua',
  member_added:       'Anëtar u shtua',
  member_removed:     'Anëtar u hoq',
  note_added:         'Shënim u shtua',
  note_edited:        'Shënim u përditësua',
  note_deleted:       'Shënim u fshi',
  document_uploaded:  'Dokument u ngarkua',
  document_deleted:   'Dokument u fshi',
};

export default function ProjectDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', variant: 'info' });
  const [editOpen, setEditOpen] = useState(false);

  const notify = (message, variant = 'info') => setToast({ open: true, message, variant });

  const workerMap = useMemo(() => {
    const m = new Map();
    workers.forEach((w) => m.set(String(w.Id || w.id), w));
    return m;
  }, [workers]);
  const clientMap = useMemo(() => {
    const m = new Map();
    clients.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [clients]);

  const loadProject = useCallback(async () => {
    try {
      const r = await apiService().getById('projects', id);
      const rec = Array.isArray(r.data) ? r.data[0] : r.data;
      setProject(rec || null);
    } catch { notify('Gabim në ngarkimin e projektit', 'error'); }
    finally { setLoading(false); }
  }, [id]);

  const loadDocs = useCallback(async () => {
    try { const r = await apiService().get(`projects/${id}/documents`); setDocs(r.data || []); } catch {}
  }, [id]);
  const loadSummary = useCallback(async () => {
    try { const r = await apiService().get(`projects/${id}/summary`); setSummary(r.data || null); } catch {}
  }, [id]);
  const loadActivities = useCallback(async () => {
    try { const r = await apiService().get(`projects/${id}/activities`); setActivities(r.data || []); } catch {}
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadProject();
    loadDocs(); loadActivities(); loadSummary();
    (async () => {
      try { const w = await apiService().get('workers'); setWorkers(w.data || []); } catch {}
      try { const c = await apiService().get('clients'); setClients(c.data || []); } catch {}
    })();
  }, [id, loadProject, loadDocs, loadActivities, loadSummary]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/projects')} sx={{ textTransform: 'none' }}>
          Kthehu te projektet
        </Button>
        <Typography sx={{ mt: 3 }} color="text.secondary">Projekti nuk u gjet.</Typography>
      </Box>
    );
  }

  const st  = statusOption(project.status || project.Status);
  const pr  = priorityOption(project.priority || project.Priority);
  const dep = String(project.department || project.Department || '').toLowerCase();
  const mgr = workerMap.get(String(project.manager_id || project.ManagerId || ''));
  const members = (Array.isArray(project.members) ? project.members : (project.Members || []))
    .map((mid) => workerMap.get(String(mid))).filter(Boolean);
  const client = clientMap.get(String(project.client_id || project.ClientId || ''));

  const refreshActivity = () => { loadActivities(); };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Toast {...toast} />
      <ProjectFormDialog
        open={editOpen}
        project={project}
        workers={activeWorkers(workers)}
        clients={clients}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); notify('Projekti u përditësua', 'success'); loadProject(); refreshActivity(); }}
      />

      {/* ── Header ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/projects')}
            sx={{ textTransform: 'none', mb: 1.5, ml: -1 }} size="small">
            Projektet
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                <Chip label={project.code || project.Code || '—'} size="small"
                  sx={{ fontFamily: 'monospace', fontWeight: 700, bgcolor: 'grey.100' }} />
                <Chip label={st.label} color={st.color} size="small" />
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: pr.color }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{pr.label}</Typography>
                </Box>
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{project.name || project.Name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {departmentLabel(dep)}{client ? ` · ${client.name}` : ''}
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}>
              Edito projektin
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1 }}>
          {/* «Shënime» u hoq nga faqja: ishte bosh te çdo projekt, dhe një tab
              që nuk mban kurrë gjë vetëm sa e ndan vëmendjen nga ato që mbajnë.
              Rruga e serverit `projects/:id/notes` dhe të dhënat e saj mbeten
              të paprekura; komponenti rri te historiku i git-it nëse duhet. */}
          <Tab icon={<DescIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Përmbledhje" sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<UploadFileIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`Dokumente (${docs.length})`} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<TimelineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Aktiviteti" sx={{ textTransform: 'none', minHeight: 48 }} />
        </Tabs>

        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {tab === 0 && (
            <OverviewTab project={project} dep={dep} mgr={mgr} members={members} client={client}
              st={st} pr={pr} canEdit onEdit={() => setEditOpen(true)} />
          )}
          {tab === 1 && (
            <DocumentsTab projectId={id} docs={docs} workerMap={workerMap}
              reload={() => { loadDocs(); refreshActivity(); }} notify={notify} />
          )}
          {tab === 2 && (
            <ActivityTab activities={activities} workerMap={workerMap} summary={summary} project={project} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

/* ─────────────────────────── Overview ─────────────────────────── */
function Field({ label, children }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.3 }}>{children}</Box>
    </Grid>
  );
}

/**
 * Çfarë i mungon këtij projekti.
 *
 * Projektet e krijuara para se këto fusha të ekzistonin i kanë të gjitha bosh,
 * dhe një faqe me tetë viza nuk i thotë njeriut as pse janë bosh, as çfarë të
 * bëjë. Një fjali me rrugëdalje vlen më shumë se tetë vendboshe.
 */
function missingCore(project, dep, mgr) {
  const out = [];
  if (!dep) out.push('departamenti');
  if (!mgr) out.push('menaxheri');
  if (!(project.start_date || project.StartDate)) out.push('data e fillimit');
  return out;
}

function OverviewTab({ project, dep, mgr, members, client, st, pr, onEdit, canEdit }) {
  const missing = missingCore(project, dep, mgr);
  const budget = Number(project.budget_hours ?? project.BudgetHours) || 0;
  return (
    <Box>
      {missing.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2.5 }}
          action={canEdit ? (
            <Button size="small" onClick={onEdit} sx={{ textTransform: 'none' }}>Plotëso</Button>
          ) : null}
        >
          Këtij projekti i mungojnë {missing.join(', ')}. Plotësoji që të shfaqet te raportet
          dhe te ndarja e orëve.
        </Alert>
      )}
      <Grid container spacing={2.5}>
        <Field label="Kodi"><Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{project.code || project.Code || '—'}</Typography></Field>
        <Field label="Departamenti"><Typography>{departmentLabel(dep)}</Typography></Field>
        <Field label="Statusi"><Chip label={st.label} color={st.color} size="small" /></Field>
        <Field label="Prioriteti">
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: pr.color }} />
            <Typography sx={{ fontWeight: 600 }}>{pr.label}</Typography>
          </Box>
        </Field>
        <Field label="Klienti / Kompania"><Typography>{client?.name || '—'}</Typography></Field>
        <Field label="Menaxheri">
          {mgr ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>{initials(mgr)}</Avatar>
              <Typography>{workerName(mgr)}</Typography>
            </Box>
          ) : <Typography color="text.secondary">—</Typography>}
        </Field>
        <Field label="Data e fillimit"><Typography>{fmtDate(project.start_date || project.StartDate)}</Typography></Field>
        <Field label="Data e përfundimit"><Typography>{fmtDate(project.end_date || project.EndDate)}</Typography></Field>
        <Field label="Buxheti i orëve">
          <Typography sx={{ fontWeight: budget ? 600 : 400 }} color={budget ? undefined : 'text.secondary'}>
            {budget ? `${budget} orë` : '—'}
          </Typography>
        </Field>
        <Field label={`Anëtarët (${members.length})`}>
          {members.length ? (
            <AvatarGroup max={8} sx={{ justifyContent: 'flex-start', '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 } }}>
              {members.map((m) => (
                <Tooltip key={m.Id || m.id} title={workerName(m)}><Avatar>{initials(m)}</Avatar></Tooltip>
              ))}
            </AvatarGroup>
          ) : <Typography color="text.secondary">Pa anëtarë</Typography>}
        </Field>
      </Grid>

      <Divider sx={{ my: 2.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Përshkrimi
      </Typography>
      <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
        {project.description || project.Description || <Box component="span" sx={{ color: 'text.secondary' }}>Pa përshkrim</Box>}
      </Typography>

      {members.length > 0 && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupsIcon sx={{ fontSize: 16 }} /> Ekipi
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {members.map((m) => (
              <Grid item key={m.Id || m.id}>
                <Chip avatar={<Avatar>{initials(m)}</Avatar>} label={workerName(m)} variant="outlined" />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}


/* ─────────────────────────── Documents ─────────────────────────── */
function DocumentsTab({ projectId, docs, workerMap, reload, notify }) {
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('title', file.name);
      const res = await fetch(`${API_URL}/api/projects/${projectId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken()}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.error || 'upload failed');
      reload(); notify('Dokumenti u ngarkua', 'success');
    } catch { notify('Gabim në ngarkimin e dokumentit', 'error'); }
    finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${authToken()}` },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.original_name || d.title || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { notify('Gabim në shkarkim', 'error'); }
  };

  const [confirm, confirmUI] = useConfirm();

  const del = async (d) => {
    if (!(await confirm({ title: 'Fshi dokumentin', message: 'Fshi këtë dokument?', confirmLabel: 'Fshi' }))) return;
    try { await apiService().delete(`projects/${projectId}/documents`, d.id); reload(); notify('Dokumenti u fshi', 'success'); }
    catch { notify('Gabim në fshirje', 'error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button component="label" variant="contained" startIcon={<UploadFileIcon />} disabled={uploading}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}>
          {uploading ? 'Duke ngarkuar…' : 'Ngarko dokument'}
          <input hidden type="file" onChange={onPick} />
        </Button>
      </Box>
      {docs.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ py: 5 }}>Nuk ka dokumente ende</Typography>
      ) : (
        <List disablePadding>
          {docs.map((d) => (
            <ListItem key={d.id} divider
              secondaryAction={
                <Box>
                  <Tooltip title="Shkarko"><IconButton size="small" onClick={() => download(d)}><DownloadIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                  <Tooltip title="Fshi"><IconButton size="small" color="error" onClick={() => del(d)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                </Box>
              }>
              <ListItemAvatar><Avatar sx={{ bgcolor: 'grey.100', color: 'text.secondary' }}><DescIcon /></Avatar></ListItemAvatar>
              <ListItemText
                primary={<Typography sx={{ fontWeight: 600 }}>{d.title || d.original_name}</Typography>}
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {fmtSize(d.size_bytes)} · {d.uploaded_by_name || '—'} · {fmtDateTime(d.created_at)}
                  </Typography>
                } />
            </ListItem>
          ))}
        </List>
      )}
      {confirmUI}
    </Box>
  );
}

/* ─────────────────────────── Activity ─────────────────────────── */
/** Orë me një dhjetore, pa zero të kota: «6 orë», jo «6.0 orë». */
const hrs = (n) => `${(Math.round((Number(n) || 0) * 10) / 10).toString().replace('.0', '')} orë`;

/**
 * Koha e projektit — afati, orët dhe njerëzit.
 *
 * Tri burime mbahen TË NDARA, sepse u përgjigjen pyetjeve të ndryshme:
 * orët e regjistruara janë puna e bërë, caktimet janë plani i njerëzve, dhe
 * segmentet e orarit janë pjesa e turnit që i kushtohet këtij projekti. Një
 * shifër e vetme që i përzien do të dukej e saktë dhe nuk do t'i përgjigjej asnjërës.
 */
function ProjectTime({ summary, project }) {
  if (!summary) return null;
  const { timeline: tl, hours: h, assignments = [], schedule = [] } = summary;
  const hasRows = assignments.length > 0 || schedule.length > 0;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Eksporti rri te koka e bllokut që eksporton — jo te fundi i faqes,
          ku do të dukej si veprim mbi tërë projektin. Çaktivizohet kur s'ka
          çfarë të nxirret: një skedar bosh është humbje kohe, jo dalje. */}
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1.5 }}>
        <Tooltip title={hasRows ? 'Shkarko si Excel (dy fletë)' : 'S\'ka të dhëna për të eksportuar'}>
          <span>
            <Button size="small" variant="outlined" disabled={!hasRows}
              startIcon={<TableChartIcon sx={{ fontSize: '16px !important' }} />}
              onClick={() => exportProjectTimeExcel(project, summary)}
              sx={{ textTransform: 'none' }}>Excel</Button>
          </span>
        </Tooltip>
        <Tooltip title={hasRows ? 'Shkarko skedarin PDF' : 'S\'ka të dhëna për të eksportuar'}>
          <span>
            <Button size="small" variant="outlined" disabled={!hasRows}
              startIcon={<PictureAsPdfIcon sx={{ fontSize: '16px !important' }} />}
              onClick={() => exportProjectTimePdf(project, summary)}
              sx={{ textTransform: 'none' }}>PDF</Button>
          </span>
        </Tooltip>
        {/* Printimi dhe shkarkimi janë i NJËJTI dokument, jo dy faqosje: e para
            e hap me dialogun gati, e dyta e ruan si skedar. */}
        <Tooltip title={hasRows ? 'Hap me dialogun e printimit' : 'S\'ka të dhëna për të printuar'}>
          <span>
            <Button size="small" variant="outlined" disabled={!hasRows}
              startIcon={<PrintIcon sx={{ fontSize: '16px !important' }} />}
              onClick={() => printProjectTime(project, summary)}
              sx={{ textTransform: 'none' }}>Printo</Button>
          </span>
        </Tooltip>
      </Stack>
      {/* ── Afati ─────────────────────────────────────────────────────────── */}
      {tl.hasDates ? (
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Afati
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {fmtDate(tl.startDate)} → {fmtDate(tl.endDate)}
            </Typography>
            {tl.remainingDays !== null && (
              <Chip
                size="small"
                color={tl.overdue ? 'error' : tl.remainingDays <= 7 ? 'warning' : 'success'}
                label={tl.overdue
                  ? `${Math.abs(tl.remainingDays)} ditë vonesë`
                  : tl.notStarted ? `fillon për ${Math.abs(tl.elapsedDays || 0)} ditë` : `${tl.remainingDays} ditë të mbetura`}
                sx={{ height: 20, fontSize: '.68rem', fontWeight: 700 }}
              />
            )}
          </Stack>
          {tl.percent !== null && (
            <>
              <LinearProgress
                variant="determinate"
                value={tl.percent}
                color={tl.overdue ? 'error' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {tl.elapsedDays} nga {tl.totalDays} ditë kanë kaluar
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Pa datë fillimi dhe mbarimi nuk ka afat për të matur — vendosi te «Edito projektin».
        </Typography>
      )}

      {/* ── Orët ──────────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Të regjistruara
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{hrs(h.logged)}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Buxheti
          </Typography>
          <Typography sx={{ fontWeight: 700 }} color={h.budget ? undefined : 'text.secondary'}>
            {h.budget ? hrs(h.budget) : '—'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Plani i caktimeve
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{hrs(h.plannedFromAssignments)}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Nga orari
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{hrs(h.scheduleHours)}</Typography>
        </Grid>
      </Grid>

      {/* Një «0 orë» pa shpjegim lexohet si «askush s'ka punuar». */}
      {!h.trackingInUse && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          Regjistrimi i orëve për projekte nuk është në përdorim ende, ndaj «të regjistruara»
          është zero. Numrat më poshtë janë <strong>plan</strong>: sa është caktuar dhe sa
          parashikon orari.
        </Alert>
      )}

      {/* ── Kush ──────────────────────────────────────────────────────────── */}
      {assignments.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Caktimet ({assignments.length})
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {assignments.map((a, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center"
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>{initials({ FirstName: a.name })}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{a.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.hoursPerDay} orë/ditë · {fmtDate(a.startDate)} → {fmtDate(a.endDate)} · {a.workdays} ditë pune
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{hrs(a.plannedHours)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {schedule.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Nga orari i punës ({schedule.length})
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {schedule.map((r) => (
              <Stack key={r.workerId} direction="row" spacing={1} alignItems="center"
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>{initials({ FirstName: r.name })}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.segments} segmente · {fmtDate(r.fromDate)} → {r.toDate ? fmtDate(r.toDate) : 'pa afat'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{hrs(r.hours)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {assignments.length === 0 && schedule.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Askush nuk është caktuar te ky projekt dhe asnjë orar nuk i referohet.
        </Typography>
      )}

      <Divider sx={{ my: 2.5 }} />
    </Box>
  );
}

function ActivityTab({ activities, workerMap, summary, project }) {
  const describe = (a) => {
    const meta = parseMeta(a.meta);
    const wName = (wid) => workerName(workerMap.get(String(wid))) || `#${wid}`;
    switch (a.type) {
      case 'status_changed':     return `${statusOption(meta.from).label} → ${statusOption(meta.to).label}`;
      case 'priority_changed':   return `${priorityOption(meta.from).label} → ${priorityOption(meta.to).label}`;
      case 'department_changed': return `${departmentLabel(meta.from)} → ${departmentLabel(meta.to)}`;
      case 'manager_changed':    return `${meta.from ? wName(meta.from) : '—'} → ${meta.to ? wName(meta.to) : '—'}`;
      case 'member_added':
      case 'member_removed':     return wName(meta.worker_id);
      case 'note_added':
      case 'note_edited':
      case 'note_deleted':       return meta.title || '';
      case 'document_uploaded':
      case 'document_deleted':   return meta.title || meta.original_name || '';
      default: return '';
    }
  };
  return (
    <Box>
      <ProjectTime summary={summary} project={project} />

      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Ngjarjet
      </Typography>
      {!activities.length ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>Nuk ka ngjarje ende.</Typography>
      ) : (
      <Box sx={{ position: 'relative', pl: 2, mt: 1.5 }}>
      <Box sx={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, bgcolor: 'divider' }} />
      <Stack spacing={2}>
        {activities.map((a) => (
          <Box key={a.id} sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', left: -17, top: 4, width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main', border: '2px solid #fff' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {ACTIVITY_LABELS[a.type] || a.type}
                {describe(a) ? <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> — {describe(a)}</Box> : null}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {a.actor_name || 'Sistemi'} · {fmtDateTime(a.created_at)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
      </Box>
      )}
    </Box>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */
function initials(w) {
  const f = (w.FirstName || w.first_name || '').charAt(0);
  const l = (w.LastName || w.last_name || '').charAt(0);
  return (f + l).toUpperCase() || '?';
}
function parseMeta(m) {
  if (!m) return {};
  if (typeof m === 'object') return m;
  try { return JSON.parse(m); } catch { return {}; }
}
